import { useEffect, useState } from "react";
import { Button, TextInput } from "../../components";
import { Timestamp } from "firebase/firestore";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  createSchedule,
  updateSchedule,
} from "../../redux/features/applicationsSlice";
import scheduleEmailTemplate from "../../utils/emailTemplates/firstRoundSchedule";
import rescheduleEmailTemplate from "../../utils/emailTemplates/roundRescheduled";
import { sendEmailHandler } from "../../utils/helperFunctions/sendEmailHandler";
import dbService from "../../firebase/dbService";

const Schedule = ({ type, setShowScheduleModule, candidate }) => {
  const schedule = candidate?.schedules?.filter(
    (schedule) => !schedule.attempted
  )[0];
  const [date, setDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (type === "reschedule" && schedule) {
      setDate(
        new Date(schedule?.scheduleDate?.seconds * 1000)
          .toLocaleString("en-CA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })
          .replace(/,/g, "")
          .replace(/\s/g, "T")
      );
    }
  }, [schedule, type]);

  useEffect(() => {
    if (date) {
      setExpiryDate(
        new Date(new Date(date).setDate(new Date(date).getDate() + 2))
          .toLocaleString("en-CA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })
          .replace(/,/g, "")
          .replace(/\s/g, "T")
      );
    }
  }, [date]);

  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toISOString().slice(0, 16);
  };

  const getMaxDateTime = () => {
    const now = new Date();
    now.setDate(now.getDate() + 7);
    return now.toISOString().slice(0, 16);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const scheduleDate = new Date(date);
    const expDate = new Date(expiryDate);

    setLoading(true);
    try {
      if (type === "reschedule") {
        const schedule = candidate.schedules?.filter(
          (schedule) => !schedule.attempted
        )[0];
        await rescheduleAssessment(scheduleDate, expDate, schedule);
      } else if (type === "schedule") {
        await scheduleAssessment(scheduleDate, expDate);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const scheduleAssessment = async (scheduleDate, expDate) => {
    const jobId = candidate?.appliedJobId;
    const job = await dbService.getdocument("jobsPosted", jobId);
    const assessmentId = job.rounds?.filter(
      (r) => r.roundType === "assessment"
    )[0]?.id;

    // add schedule data to db 'assessmentTests'
    const examCode = (Math.random() + 1).toString(36).substring(6);
    const scheduleData = {
      candidateId: candidate.candidateId,
      applicationId: candidate?.id,
      jobId: candidate?.appliedJobId,
      assessmentId,
      candidateEmail: candidate?.email,
      companyName: candidate?.appliedAtCompany,
      examCode,
      scheduleDate: Timestamp.fromDate(scheduleDate),
      expDate: Timestamp.fromDate(expDate),
      attempted: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    try {
      await dispatch(createSchedule({ data: scheduleData }));
      toast.success("Success! The Assessment has been scheduled.");
      await sendEmail("schedule", examCode, scheduleDate, expDate);
    } catch (error) {
      console.log(error);
    } finally {
      setShowScheduleModule(false);
    }
  };

  const rescheduleAssessment = async (scheduleDate, expDate, schedule) => {
    const rescheduleData = {
      scheduleDate: Timestamp.fromDate(scheduleDate),
      expDate: Timestamp.fromDate(expDate),
      updatedAt: Timestamp.now(),
    };
    try {
      await dispatch(
        updateSchedule({
          data: rescheduleData,
          scheduleId: schedule.id,
          applicationId: candidate?.id,
        })
      );
      toast.success("Success! The Assessment has been rescheduled.");
      await sendEmail("reschedule", schedule.examCode, scheduleDate, expDate);
    } catch (error) {
      console.log(error);
    } finally {
      setShowScheduleModule(false);
    }
  };

  const sendEmail = async (type, examCode, scheduleDate, expDate) => {
    const dateFormat = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    const timeFormat = { hour: "numeric", minute: "2-digit", hour12: true };
    const emailDetails = {
      candidateName: candidate?.name,
      candidateEmail: candidate?.email,
      examCode,
      jobTitle: candidate?.appliedPosition,
      companyName: candidate?.appliedAtCompany,
      startDate: {
        date: scheduleDate?.toLocaleDateString("en-IN", dateFormat),
        time: scheduleDate?.toLocaleTimeString("en-IN", timeFormat),
      },
      expDate: {
        date: expDate?.toLocaleDateString("en-IN", dateFormat),
        time: expDate?.toLocaleTimeString("en-IN", timeFormat),
      },
    };
    try {
      const { subject, body } =
        type === "schedule"
          ? scheduleEmailTemplate(emailDetails)
          : type === "reschedule"
          ? rescheduleEmailTemplate(emailDetails)
          : null;

      const successMessage =
        type === "schedule"
          ? "The Schedule email has been sent to the candidate."
          : "The Reschedule email has been sent to the candidate.";

      await sendEmailHandler({
        emailId: candidate.email,
        subject,
        body,
        successMessage,
      });
    } catch (error) {
      console.log(error);
      throw new Error(error);
    }
  };

  return (
    <form className="p-8 space-y-4 min-w-[500px]" onSubmit={handleSubmit}>
      <h1 className="heading2">{type} Assessment</h1>

      <div>
        <p className="text-xs font-medium">Select Assessment Date and Time :</p>
        <label className="text-xs font-medium" htmlFor="datetime">
          From:
        </label>
        <div className="flex border-2 rounded-xl overflow-hidden">
          <TextInput
            type="datetime-local"
            inputName="datetimeFrom"
            inputValue={date}
            isRequired={true}
            handleChange={(e) => setDate(e.target.value)}
            min={getCurrentDateTime()}
            max={getMaxDateTime()}
          />
        </div>
        <label className="text-xs font-medium" htmlFor="datetime">
          To :
        </label>
        <div className="flex border-2 rounded-xl overflow-hidden">
          <TextInput
            type="datetime-local"
            inputName="datetimeTo"
            inputValue={expiryDate}
            isDisabled={true}
          />
        </div>
      </div>
      <div className="flex w-full justify-end">
        {error && (
          <p className="text-red-500 text-xs self-center w-full">{error}</p>
        )}
        <Button type="submit" loading={loading}>
          <span className="capitalize">{type}</span>
        </Button>
      </div>
    </form>
  );
};

export default Schedule;
